import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import customFetch from '../../../../utils/customFetch';
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useNavigation } from 'react-router-dom';
import { toast } from 'react-toastify';
import NewCardSlideIn from './NewCardSlideIn';

// Query-Funktion mit URL-Parametern
const allCardsQuery = (searchParams) => ({
  queryKey: ['allCards', searchParams.toString()],
  queryFn: async () => {
    const params = new URLSearchParams(searchParams);
    const response = await customFetch.get(`/cards?${params.toString()}`);
    return response.data;
  },
});

export const loader =
  (queryClient) =>
  async ({ request }) => {
    const url = new URL(request.url);
    const searchParams = url.searchParams;
    return await queryClient.ensureQueryData(allCardsQuery(searchParams));
  };

const CardList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSlideIn, setShowSlideIn] = useState(false);
  const queryClient = useQueryClient();

  // URL-Parameter auslesen
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentStatus = searchParams.get('status') || '';
  const limit = parseInt(searchParams.get('limit')) || 25;

  // Query mit aktuellen Parametern
  const { data, isLoading, error } = useQuery(allCardsQuery(searchParams));

  if (isLoading) return <div className="p-6">Lädt...</div>;
  if (error)
    return <div className="p-6 text-red-600">Fehler beim Laden der Karten</div>;

  const {
    cards = [],
    totalCards,
    numOfPages,
    currentPage: serverCurrentPage,
  } = data;

  console.log(cards);

  // Filter-Handler
  const handleStatusFilter = (status) => {
    const newParams = new URLSearchParams(searchParams);
    if (status === '') {
      newParams.delete('status');
    } else {
      newParams.set('status', status);
    }
    newParams.set('page', '1'); // Reset auf erste Seite bei Filteränderung
    setSearchParams(newParams);
  };

  // Pagination-Handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= numOfPages) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', newPage.toString());
      setSearchParams(newParams);
    }
  };

  // Status-Optionen
  const allStatuses = [
    'Alle Stati',
    'Aktiv',
    'Blockiert',
    'Demo',
    'Nicht beansprucht',
    'Ausstehend',
  ];

  const statusValueMap = {
    'Alle Stati': '',
    Aktiv: 'active',
    Blockiert: 'blocked',
    Demo: 'demo',
    'Nicht beansprucht': 'unclaimed',
    Ausstehend: 'pending',
  };

  const statusDisplayMap = {
    '': 'Alle Stati',
    active: 'Aktiv',
    blocked: 'Blockiert',
    demo: 'Demo',
    unclaimed: 'Nicht beansprucht',
    pending: 'Ausstehend',
  };

  const setStatusFilter = (statusLabel) => {
    const statusValue = statusValueMap[statusLabel];
    handleStatusFilter(statusValue);
  };

  // Pagination-Hilfsfunktionen
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(numOfPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Datum formatieren
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Kartenadministration
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Hier sind alle Karten aufgelistet. ({totalCards || cards.length}{' '}
              Karten gefunden)
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              className="block rounded-md bg-svisit-primary-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
              onClick={() => setShowSlideIn(true)}
            >
              Karte hinzufügen
            </button>
          </div>
        </div>

        {/* Filter-Bereich entfernt - jetzt im Tabellenheading */}

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Besitzer
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      <Menu
                        as="div"
                        className="relative inline-block text-left"
                      >
                        <MenuButton className="group inline-flex items-center focus:outline-none">
                          Status
                          <span
                            className={`ml-2 flex-none rounded-sm text-gray-900 group-hover:bg-gray-200 ${
                              currentStatus
                                ? 'bg-svisit-primary-100'
                                : 'bg-gray-100'
                            }`}
                          >
                            <ChevronDownIcon
                              className="size-5"
                              aria-hidden="true"
                            />
                          </span>
                        </MenuButton>
                        <MenuItems
                          transition
                          className="absolute left-0 z-10 mt-1 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                        >
                          <div className="py-1">
                            {allStatuses.map((status) => (
                              <MenuItem key={status}>
                                <button
                                  onClick={() => setStatusFilter(status)}
                                  className={`block w-full px-4 py-2 text-left text-sm ${
                                    statusDisplayMap[currentStatus] === status
                                      ? 'bg-gray-100 text-gray-900'
                                      : 'text-gray-700'
                                  } data-focus:bg-gray-100 data-focus:text-gray-900 data-focus:outline-hidden`}
                                >
                                  {status}
                                </button>
                              </MenuItem>
                            ))}
                          </div>
                        </MenuItems>
                      </Menu>
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Produkt
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Hinzugefügt am
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                    >
                      <span className="sr-only">Bearbeiten</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {cards.map((card) => (
                    <tr key={card._id} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {card._id}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <a
                          href={`/dashboard/benutzerverwaltung/${card.owner?._id}`}
                          className="cursor-pointer hover:text-gray-700"
                        >
                          {card.owner?.firstName} {card.owner?.lastName}
                        </a>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {card.status === 'active' ? (
                          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            {card.status}
                          </span>
                        ) : card.status === 'blocked' ? (
                          <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                            {card.status}
                          </span>
                        ) : card.status === 'demo' ? (
                          <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20">
                            {card.status}
                          </span>
                        ) : card.status === 'unclaimed' ? (
                          <span className="inline-flex items-center rounded-full bg-fuchsia-50 px-2 py-1 text-xs font-medium text-fuchsia-700 ring-1 ring-inset ring-fuchsia-600/20">
                            {card.status}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                            {card.status}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {card.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {formatDate(card.createdAt)}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <a
                          href={`/dashboard/kartenadministration/${card._id}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-900"
                        >
                          Bearbeiten
                          <span className="sr-only">, {card._id}</span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Paginierung */}
              {numOfPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Zurück
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= numOfPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Weiter
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Zeige{' '}
                        <span className="font-medium">
                          {(currentPage - 1) * limit + 1}
                        </span>{' '}
                        bis{' '}
                        <span className="font-medium">
                          {Math.min(
                            currentPage * limit,
                            totalCards || cards.length
                          )}
                        </span>{' '}
                        von{' '}
                        <span className="font-medium">
                          {totalCards || cards.length}
                        </span>{' '}
                        Ergebnissen
                      </p>
                    </div>
                    <div>
                      <nav
                        className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Vorherige</span>
                          <ChevronLeftIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </button>

                        {getPageNumbers().map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                              pageNum === currentPage
                                ? 'z-10 bg-svisit-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600'
                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                            }`}
                          >
                            {pageNum}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= numOfPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Nächste</span>
                          <ChevronRightIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Slide-In für neue Karte */}
      <NewCardSlideIn
        showSlideIn={showSlideIn}
        setShowSlideIn={setShowSlideIn}
      />
    </>
  );
};

export default CardList;
